// components/Disc/discGeometry.js
//
// Geometry helpers shared by both discs — the skills disc (DrawDisc) and the
// standards disc (DrawStandardsDisc) — so their ring layout stays identical and
// the math lives in exactly one place. See DrawDisc.jsx for the coordinate
// system (normalized x/dx angles, y/dy radii).

import { yLevelsMapper } from './DiscMappers';

// Hex (#rrggbb) → "rgba(r,g,b,a)". Falls back to transparent-ish black on a
// malformed hex so a bad color never throws mid-render.
export function rgbaFromHex(hex, a) {
  const h = String(hex || '')
    .replace('#', '')
    .trim();
  if (h.length !== 6) return `rgba(0,0,0,${a})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// A node's radial band (inner radius y + thickness dy) is determined purely by
// its depth (1 = inner ring … 3 = outer ring) via yLevelsMapper.
export function setRadial(node) {
  const m = yLevelsMapper[node.depth] || yLevelsMapper[0];
  node.y = m.y;
  node.dy = m.dy;
}

// Assigns every node its angular span (x = start, dx = width) so a parent's
// children exactly fill the parent's wedge, split proportionally by weight.
//
// `weight(node)` is INJECTED because the two discs size their outer ring
// differently: the skills disc weights a Skill by how many specific skills it
// has; the standards disc weights every standard equally. Gaps are placed only
// BETWEEN siblings (n children → n-1 gaps; none at the wedge ends) so arcs tile
// seamlessly, and the gap is clamped so it can never exceed a narrow wedge.
export function layoutAngles(root, weight, gapByLevel = 0.004) {
  function rec(parent) {
    if (!parent.children || parent.children.length === 0) return;

    const numChildren = parent.children.length;

    // A lone child inherits the parent's full span (no gaps needed).
    if (numChildren === 1) {
      parent.children[0].x = parent.x;
      parent.children[0].dx = parent.dx;
      rec(parent.children[0]);
      return;
    }

    const weights = parent.children.map(weight);
    const totalWeight = weights.reduce((a, b) => a + b, 0) || 1;

    // Clamp the per-gap size, then `usable` is the width left for actual arcs.
    const effectiveGap = Math.min(gapByLevel, parent.dx / (numChildren * 2));
    const effectiveTotalGaps = effectiveGap * (numChildren - 1);
    const usable = Math.max(0, parent.dx - effectiveTotalGaps);

    let cursor = parent.x;
    for (let i = 0; i < numChildren; i++) {
      const c = parent.children[i];
      const share = usable * (weights[i] / totalWeight);
      c.x = cursor;
      c.dx = share;
      cursor += share;
      // Gap after every child except the last (gaps go between siblings only).
      if (i < numChildren - 1) cursor += effectiveGap;
      rec(c);
    }
  }

  rec(root);
}
